import {Injectable} from "@angular/core";
import {VizixService} from "./vizix.service";
import {SQLite, SQLiteObject} from "@ionic-native/sqlite";
import {Platform} from "ionic-angular";
import {Network} from "@ionic-native/network";
import {EPCSequenceThing} from "../models/EPCSequenceThing";
import {VizixEventService} from "./vizix.event.service";
import {PatchThingRequestItem} from "../models/PatchThingRequestItem";
import {EPC_SEQUENCE_UDF_ENCODE_EPC, EPC_SEQUENCE_UDF_WRITE_TIME} from "../app/app.constants";
import {Subscription} from "rxjs/Subscription";


@Injectable()
export class EPCSequenceThingService {

  private tag = '[EPC_SEQUENCE_THING_SERVICE]';

  private DB_NAME = 'EpcSequenceThing.db';
  private DB_LOCATION = 'default';
  private DB_TABLE_EPC_SEQUENCE_SERVICE = 'epcSequenceThing';
  private db: SQLiteObject;
  private isConnected: boolean = false;
  private isCordova: boolean = false;
  private isCheckingPending: boolean = false;

  private TABLE_COLUMN_SERIAL_SEQUENCE = 'SerialSequence';
  private TABLE_COLUMN_ENCODED_EPC = 'EncodedEPC';
  private TABLE_COLUMN_WRITE_TIME = 'WriteTime';
  private TABLE_COLUMN_WRITE_GROUP = 'GroupSequence';

  private disconnectedSubscription: Subscription;
  private connectedSubscription: Subscription;

  constructor(public platform: Platform,
              private network: Network,
              private vizixService: VizixService,
              private sqlite: SQLite,) {
  }

  /**
   * Initializes the database (if not exists) and starts the service
   */
  public init(skipUpload?: boolean) {
    console.log(this.tag, 'Initializing EPCSequenceThing Service');

    // Init database and table if table not exists
    //  EPCSequenceThing Table columns:

    if (this.platform.is('cordova') && !this.platform.is('core')) {
      this.isCordova = true;
      console.log(this.tag, 'Platform is mobile, init db');

      this.sqlite.create({name: this.DB_NAME, location: this.DB_LOCATION}).then((db: SQLiteObject) => {
        this.db = db;
        this.db.executeSql('create table if not exists ' + this.DB_TABLE_EPC_SEQUENCE_SERVICE +
          '(' + this.TABLE_COLUMN_SERIAL_SEQUENCE + ' VARCHAR(64), ' +
          this.TABLE_COLUMN_ENCODED_EPC + ' VARCHAR(64), ' +
          this.TABLE_COLUMN_WRITE_TIME + ' VARCHAR(64), ' +
          this.TABLE_COLUMN_WRITE_GROUP + ' VARCHAR(64))', {})
          .then(() => {
            console.log(this.tag, 'table ' + this.DB_TABLE_EPC_SEQUENCE_SERVICE + ' created');
            this.initService();
          })
          .catch(e => {
            this.isCordova = false;
          });
      }).catch(e => console.log(this.tag, e));
    } else {
      console.log(this.tag, 'Platform is not mobile, skipping database');
      this.isCordova = false;
      this.initService();
    }
  }


  public deleteDataBase() {
    if (this.isCordova) {
      this.sqlite.deleteDatabase({name: this.DB_NAME, location: this.DB_LOCATION}).then(() => {
        console.log(this.tag, 'Delete DB Encode Tgs successful');
      }, err_ => {
        console.log(this.tag, 'ERROR Delete DB EPCSequenceThing');
      });
    } else {
      console.log(this.tag, 'Platform is not cordova! Cant delete database');
    }
  }


  /**
   * Starts the service, checks for pending Encoded Tgs to upload
   * It is aware of network connection
   */
  private initService(skipUpload?: boolean) {

    // Check if network connection is available
    if (this.network.type != 'none' && this.network.type != 'unknown') {
      // If available, then check if pending Encoded Tgs
      console.log(this.tag, 'network state: ' + this.network.type);
      this.isConnected = true;

      if (!skipUpload) {
        setTimeout(() => { // Wait 5 seconds on service startup to check if pending items (to init db)
          this.checkPending();
        }, 5000);
      }

    } else {
      console.log(this.tag, 'Not connected, waiting for network connection availability');
    }

    // Called when network changes from disconnected to connected
    if(!this.connectedSubscription) {
      this.connectedSubscription = this.network.onConnect().subscribe(() => {
        console.log(this.tag, 'network connected!');
        this.isConnected = true;
        this.checkPending();
      });
    }

    // Called when network changes from connected to disconnected
    if(!this.disconnectedSubscription) {
      this.disconnectedSubscription = this.network.onDisconnect().subscribe(() => {
        console.log(this.tag, 'network disconnected!');
        this.isConnected = false;
      });
    }
  }

  /**
   * Uploads EncodeTags object to Vizix, if it fails, saves it locally
   * @param encodedTag
   * @param fromLocalDatabase
   */

  public saveEPCSequenceThing(epcSequenceThing: EPCSequenceThing, fromLocalDatabase?: boolean): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (
        epcSequenceThing.WriteTime &&
        epcSequenceThing.EncodedEPC &&
        epcSequenceThing.SerialSequence) {


        let body: PatchThingRequestItem[] = [];
        let requestItem = new PatchThingRequestItem();

        requestItem.serialNumber = epcSequenceThing.SerialSequence;
        requestItem.thingTypeCode = this.vizixService.configurationApp.EncodeTagConfiguration.EncodeSerialThingTypeCode.value;
        requestItem.group = epcSequenceThing.Group;

        requestItem.udfs = {};
        requestItem.udfs[EPC_SEQUENCE_UDF_ENCODE_EPC] = {value: epcSequenceThing.EncodedEPC};
        requestItem.udfs[EPC_SEQUENCE_UDF_WRITE_TIME] = {value: epcSequenceThing.WriteTime};

        body.push(requestItem);

        // TODO: MOVE VIZIX SERVICE PATCH METHOD
        this.vizixService.VizixPatchItem(
          body,
        ).subscribe((data) => {
          if (data && data[requestItem.serialNumber] && data[requestItem.serialNumber].ERROR) {
            console.log(this.tag, 'Error in patch epcSequenceThing, saving locally', data);
            reject();
          } else {
            console.log(this.tag, 'epcSequenceThing recorded in Vizix');
            resolve(true);
          }
        }, (error) => {
          if (!fromLocalDatabase) this.saveEPCSequenceThingLocally(epcSequenceThing);
          resolve(false);
        });
      } else {
        console.log(this.tag, 'Invalid EPCSequenceThing!');
        reject();
      }
    });
  }

  /**
   * Saves EpcSequenceThing object to database (as String)
   * @param epcSequenceThing
   */
  private saveEPCSequenceThingLocally(epcSequenceThing: EPCSequenceThing) {
    if (this.isCordova) {
      this.db.executeSql('INSERT INTO ' + this.DB_TABLE_EPC_SEQUENCE_SERVICE + ' VALUES(?,?,?,?)',
        [
          epcSequenceThing.SerialSequence,
          epcSequenceThing.EncodedEPC,
          epcSequenceThing.WriteTime,
          epcSequenceThing.Group])
        .then(res => {
          console.log(this.tag, 'Inserted', epcSequenceThing);
        }).catch((error) => {
        //TODO Catch values to complete errors.
      })
    } else {
      console.log(this.tag, 'Platform is not cordova! Cant save Sequence EPC locally');
    }
  }

  /**
   * Checks if there are Encoded Tags saved locally to upload
   */
  private checkPending() {
    if (!this.isCheckingPending) {
      this.isCheckingPending = true;
      if (this.isCordova) {
        if (this.isConnected) {
          console.log(this.tag, 'checking pending');
          // Try to upload EPCSequenceThing information from db.
          // it is not necessary to group them, just upload
          this.db.executeSql('select * from ' + this.DB_TABLE_EPC_SEQUENCE_SERVICE, {})
            .then(res => {
              console.log(this.tag, 'EpcSequenceThing to upload: ' + res.rows.length);
              // Convert response from sql to list of EncodeTags

              let epcSequenceThingList: EPCSequenceThing[] = [];
              for (let i = 0; i < res.rows.length; i++) {
                console.log(this.tag, 'Processing EPCSequenceThing', res.rows.item(i));
                if (res.rows.item(i)[this.TABLE_COLUMN_SERIAL_SEQUENCE]) {
                  epcSequenceThingList.push(new EPCSequenceThing(
                    res.rows.item(i)[this.TABLE_COLUMN_SERIAL_SEQUENCE],
                    res.rows.item(i)[this.TABLE_COLUMN_ENCODED_EPC],
                    res.rows.item(i)[this.TABLE_COLUMN_WRITE_TIME],
                    res.rows.item(i)[this.TABLE_COLUMN_WRITE_GROUP]));
                } else {
                  console.log(this.tag, 'Invalid encoded tag from database!', res.rows);
                }
              }

              console.log(this.tag, 'EPCSequences list', epcSequenceThingList);

              if (epcSequenceThingList.length > 0) {
                for (let epcSequenceThingItem  of epcSequenceThingList) {
                  this.saveEPCSequenceThing(epcSequenceThingItem, true).then((save) => {
                    if (save) {
                      this.removeEncodeSequenceThingFromDB(epcSequenceThingItem);
                      this.isCheckingPending = false;
                    }
                  });
                }
              } else {
                this.isCheckingPending = false;
                console.log(this.tag, 'No pending EPCSequences to upload');
              }

            }).catch(e => console.log(this.tag, 'Error getting EPCSequences from database'));

        } else {
          console.log(this.tag, 'Not connected! waiting for internet connection');
        }
      } else {
        console.log(this.tag, 'Platform is not cordova! Cant check pending EPCSequences');
      }
    } else {
      console.log(this.tag, 'Service is already checking pending EPCSequences to upload');
    }
  }

  /**
   * Once uploaded successfully, the local Encoded Tag is removed from db
   * @param encodedTag
   */
  private removeEncodeSequenceThingFromDB(epcSequenceThing: EPCSequenceThing) {
    if (this.isCordova) {
      this.db.executeSql("DELETE FROM " + this.DB_TABLE_EPC_SEQUENCE_SERVICE +
        " WHERE " + this.TABLE_COLUMN_SERIAL_SEQUENCE + " = '" + epcSequenceThing.SerialSequence +
        "' AND " + this.TABLE_COLUMN_ENCODED_EPC + " = '" + epcSequenceThing.EncodedEPC +
        "' AND " + this.TABLE_COLUMN_WRITE_TIME + " = '" + epcSequenceThing.WriteTime +
        "' AND " + this.TABLE_COLUMN_WRITE_GROUP + " = '" + epcSequenceThing.Group + "'", [])
        .then(res => {
          console.log(this.tag, 'Removed EPCSequenceThing from db ', res);
        })
        .catch(e => {
          console.log(this.tag, e);
        });
    } else {
      console.log(this.tag, 'Platform is not cordova! Cant remove EPCSequenceThing');
    }
  }

}



// WEBPACK FOOTER //
// ./src/providers/epc.sequence.thing.service.ts